import React from 'react';
import {Form, Formik, Field} from 'formik';
import {Alert, Button, FormGroup, Label} from 'reactstrap';
import * as Yup from 'yup';

import {useUserDispatch} from 'contexts/user';
import {submitForm} from 'utils/forms';
import CustomInputField from '../OptInForm/components/CustomInputField';
import CustomCheckboxField from '../OptInForm/components/CustomCheckboxField';
import classNames from 'classnames';
import track from 'utils/track';

const SignupSchema = Yup.object().shape({
  firstName: Yup.string().required('Required'),
  email: Yup.string().email('Invalid email').required('Required'),
  sms_number: Yup.string().when('bulk_sms', {
    is: true,
    then: (s) =>
      s
        .matches(/(^\+1 \([0-9]{3}\) [0-9]{3}-[0-9]{4}$)/, 'Please enter a valid US telephone number')
        .required('Required')
  }),
  website: Yup.string().test('is-empty', 'Leave this empty.', (value) => !value)
});

export default function ContactInfoForm({
  id,
  apiUrl,
  siteId,
  optinFormId,
  contactInfoFormHeader = (
    <>
      <p className="text-center mb-4">
        <b>Enter your name and email below to sign up.</b>
      </p>
    </>
  ),
  contactInfoFormButtonText = 'Join Now',
  entry = 'default',
  onComplete = () => {}
}) {
  const userDispatch = useUserDispatch();

  return (
    <>
      {contactInfoFormHeader}
      <div className="optin-wrap mb-3">
        <Formik
          initialValues={{
            email: '',
            firstName: '',
            bulk_sms: false,
            sms_number: '',
            report: entry
          }}
          validationSchema={SignupSchema}
          onSubmit={({sms_number, ...values}, actions) => {
            return submitForm({sms_number: sms_number.replace(/\D/g, ''), ...values}, optinFormId)
              .then(({data}) => {
                window.analytics && window.analytics.identify && window.analytics.identify(data.proxy_data.user.uuid);
                return userDispatch({
                  type: 'SET_USER',
                  payload: {
                    firstName: values.firstName,
                    email: values.email,
                    registrationId: data.proxy_data.registrationId,
                    mcFormReqUUID: data.proxy_data.mc_form_uuid,
                    uuid: data.proxy_data.user.uuid
                  }
                });
              })
              .then(() =>
                track('User Registered', {
                  property: siteId,
                  entryPoint: 'Join',
                  label: `${siteId} - Join`
                })
              )
              .then(() => onComplete())
              .catch((e) => {
                actions.setStatus({error: e});
                actions.setSubmitting(false);
              });
          }}
          render={({values, handleSubmit, isSubmitting, isValid, status}) => (
            <Form onSubmit={handleSubmit}>
              {status && status.error && <Alert color="danger">{status.error}</Alert>}
              <FormGroup>
                <Label for="firstName" hidden>
                  First Name
                </Label>
                <Field
                  name="firstName"
                  type={'text'}
                  component={CustomInputField}
                  placeholder="What is your first name?"
                  bsSize="lg"
                />
              </FormGroup>
              <FormGroup>
                <Label for="email" hidden>
                  Email
                </Label>
                <Field
                  name="email"
                  type={'email'}
                  component={CustomInputField}
                  placeholder="What is your best email?"
                  bsSize="lg"
                />
              </FormGroup>

              <FormGroup>
                <Field
                  name="bulk_sms"
                  id={`${id}-bulk_sms`}
                  component={CustomCheckboxField}
                  label={
                    <>
                      Text me reminders and updates
                      <br />
                      <span className="text-small">
                        *Optional and not required to join. Text message updates are only available for US mobile
                        numbers.
                      </span>
                    </>
                  }
                  type="checkbox"
                  bsSize="lg"
                />
              </FormGroup>

              {values.bulk_sms ? (
                <FormGroup className="sms-input-group">
                  <Label for="tel" hidden>
                    Phone Number
                  </Label>
                  <Field
                    type="tel"
                    placeholder="What is your mobile number?"
                    name="sms_number"
                    component={CustomInputField}
                    mask="+1 (999) 999-9999"
                    bsSize="lg"
                  />
                </FormGroup>
              ) : null}

              <FormGroup className="d-none">
                <Label for="Website" hidden>
                  Should be left blank
                </Label>
                <Field name="website" type={'text'} component={CustomInputField} placeholder="" bsSize="lg" />
              </FormGroup>

              <Button
                className="text-uppercase text-h4 text-condensed text-800"
                color="cta"
                type="submit"
                disabled={isSubmitting || !isValid}
                block
              >
                {isSubmitting ? 'Loading...' : contactInfoFormButtonText ? contactInfoFormButtonText : 'Continue'}
              </Button>
            </Form>
          )}
        />
      </div>
      <p className="text-disclaimer">
        By entering your information here, you are agreeing to receive messages from Food Revolution Network. Your email
        address will NEVER be shared or sold. You are always free to easily unsubscribe at any time. See our{' '}
        <a
          className={classNames(`text-underline link-purple`)}
          href="https://foodrevolution.org/privacy-policy/"
          target="_blank"
          rel="noreferrer"
        >
          Privacy Policy
        </a>{' '}
        for more info.
      </p>
      <p className="text-disclaimer">
        By checking the "Send me text message reminders and updates" box you agree to receive important updates,
        reminders, and promotional messages about events and products from Food Revolution Network (FRN). Message
        frequency varies. You can stop receiving messages at any time by texting STOP to 67692, for help text HELP to
        67692 or email us at{' '}
        <a
          className={classNames(`text-underline link-purple`)}
          href="mailto:support@foodrevolution.org"
          target="_blank"
          rel="noreferrer"
        >
          support@foodrevolution.org
        </a>
        . Message and data rates may apply. By opting in for text messages, you authorize FRN to deliver marketing
        messages using an automatic telephone dialing system. SMS opt-in is not a requirement for purchasing any
        property, goods, or services. By leaving the "Send me text message reminders and updates" box unchecked you will
        not be opted in for SMS messages at this time. See our{' '}
        <a
          className={classNames(`text-underline link-purple`)}
          href="https://foodrevolution.org/privacy-policy/"
          target="_blank"
          rel="noreferrer"
        >
          Privacy Policy
        </a>{' '}
        and{' '}
        <a
          className={classNames(`text-underline link-purple`)}
          href="https://foodrevolution.org/terms-and-conditions/"
          target="_blank"
          rel="noreferrer"
        >
          Terms
        </a>{' '}
        for more info.
      </p>
    </>
  );
}
